import SubmissionSelector from "./SubmissionSelector";
import {
  EditRounded as EditIcon,
  ExposureRounded as DiffIcon,
  RefreshRounded as RefreshIcon,
} from "@mui/icons-material";
import {
  Alert,
  Chip,
  Grid,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Component from "components/Component";
import AdminDashboard from "components/admin/AdminDashboard";
import CodeEditor, {
  DiffEditor,
  EditorPreferenceToggle,
} from "components/admin/CodeEditor";
import BigLoader from "components/common/BigLoader";
import Button from "components/common/Button";
import CopyButton from "components/common/CopyButton";
import TextField from "components/common/TextField";
import DashboardContent from "components/layout/DashboardContent";
import * as config from "config";
import { detailedDiff } from "deep-object-diff";
import { useCallback, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import * as submissionsService from "services/submissionsService";
import * as utils from "utils";

const diff = (oldJson, candidateJsonText) => {
  try {
    return {
      hasError: false,
      diff: detailedDiff(oldJson, JSON.parse(candidateJsonText)),
    };
  } catch (e) {
    return {
      hasError: true,
      message: `${e.name} ${e.message}`,
    };
  }
};

const SubmissionDiff = ({
  originalState,
  newStateText,
  submissionId,
  savedToken,
  companyId,
  onSaveSuccess,
}) => {
  const [updateReason, setUpdateReason] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);

  const diffResult = diff(originalState, newStateText);
  const hasChanges =
    !diffResult.hasError &&
    (Object.keys(diffResult.diff?.added ?? {}).length > 0 ||
      Object.keys(diffResult.diff?.deleted ?? {}).length > 0 ||
      Object.keys(diffResult.diff?.updated ?? {}).length > 0);

  const handleSave = async () => {
    if (!submissionId || !savedToken) {
      setSaveError("Missing submission ID or saved token");
      return;
    }

    try {
      setIsSaving(true);
      setSaveError(null);

      const newState = JSON.parse(newStateText);
      const body = {
        state: newState,
        filename: `${submissionId}.json`,
        savedToken: savedToken,
      };

      const response = await utils.authenticatedFetch(
        config.endpoints.persistence("update"),
        {
          method: "PUT",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(body),
        },
        companyId
      );

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData?.message || "Failed to save submission");
      }

      const responseData = await response.json();
      onSaveSuccess?.(responseData);
    } catch (e) {
      setSaveError(e.message);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        {diffResult.hasError ? (
          <Alert severity={"error"}>{diffResult.message}</Alert>
        ) : !hasChanges ? (
          <Alert severity={"info"}>{"No changes detected"}</Alert>
        ) : (
          <DiffEditor
            original={JSON.stringify(originalState, null, 2)}
            modified={newStateText}
            language={"json"}
          />
        )}
      </Grid>
      {saveError && (
        <Grid item xs={12}>
          <Alert severity={"error"}>{saveError}</Alert>
        </Grid>
      )}
      {hasChanges && (
        <>
          <Grid item xs={10}>
            <TextField
              variant={"filled"}
              label={"Reason for change (optional but recommended)"}
              fullWidth
              onChange={(e) => setUpdateReason(e.target.value)}
              value={updateReason}
            />
          </Grid>
          <Grid item xs={2}>
            <Grid
              container
              justifyContent={"flex-end"}
              alignItems={"center"}
              style={{ height: "100%" }}
            >
              <Button
                variant={"contained"}
                disableElevation
                color={"primary"}
                disabled={isSaving}
                isLoading={isSaving}
                onClick={handleSave}
              >
                {"Save Changes"}
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </Grid>
  );
};

const SubmissionEditor = () => {
  const { companyId } = useParams();

  const [selectedSubmission, setSelectedSubmission] = useState(null);
  const [submissionData, setSubmissionData] = useState(null);
  const [newStateText, setNewStateText] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [loadError, setLoadError] = useState(null);
  const [differenceToggle, setDifferenceToggle] = useState(false);

  const [
    retrieveSubmission,
  ] = submissionsService.useLazyRetrieveSubmissionQuery();

  // Load submission when selected
  const loadSubmission = useCallback(async () => {
    const submissionId = selectedSubmission?.submissionId;
    if (!submissionId) return;

    try {
      setIsLoading(true);
      setLoadError(null);
      setNewStateText(null);

      const response = await retrieveSubmission({
        submissionId,
        impersonateCompanyId: companyId,
      });

      if (response.error) {
        throw new Error(response.error?.message || "Failed to load submission");
      }

      setSubmissionData(response.data);
    } catch (e) {
      setLoadError(e.message);
    } finally {
      setIsLoading(false);
    }
  }, [selectedSubmission?.submissionId, companyId, retrieveSubmission]);

  useEffect(() => {
    const submissionId = selectedSubmission?.submissionId;
    if (submissionId) {
      loadSubmission();
    }
  }, [selectedSubmission?.submissionId, loadSubmission]);

  // Reset when company changes
  useEffect(() => {
    setSelectedSubmission(null);
    setSubmissionData(null);
    setNewStateText(null);
    setLoadError(null);
  }, [companyId]);

  const handleKeyDown = useCallback(
    (event) => {
      if (event.metaKey && event.key === "d") {
        event.preventDefault();
        if (!differenceToggle) setDifferenceToggle(true);
      } else if (event.metaKey && event.key === "e") {
        event.preventDefault();
        if (differenceToggle) setDifferenceToggle(false);
      }
    },
    [differenceToggle]
  );

  useEffect(() => {
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [handleKeyDown]);

  const originalState = submissionData?.state ?? {};
  const jsonValue = newStateText ?? JSON.stringify(originalState, null, 2);

  const handleSaveSuccess = (responseData) => {
    // Update the local state with the new saved data
    setSubmissionData({
      ...submissionData,
      savedToken: responseData.savedToken,
      savedAt: responseData.savedAt,
      savedBy: responseData.savedBy,
      state: JSON.parse(newStateText),
    });
    setNewStateText(null);
    setDifferenceToggle(false);
  };

  return (
    <div className={"app"}>
      <AdminDashboard>
        <DashboardContent>
          <Component
            title={"Submission State Editor"}
            subtitle={
              "Load a submission and directly edit its JSON state. Use this to fix stuck loading states, correct data issues, or debug problems."
            }
            spacing={config.GRID_SPACING}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Stack direction={"row"} spacing={2} alignItems={"center"}>
                  <SubmissionSelector
                    companyId={companyId}
                    value={selectedSubmission}
                    onChange={setSelectedSubmission}
                    label={"Select Submission to Edit"}
                    sx={{ minWidth: "25em" }}
                  />
                  {selectedSubmission && (
                    <Button
                      startIcon={<RefreshIcon />}
                      onClick={loadSubmission}
                      disabled={isLoading}
                    >
                      {"Reload"}
                    </Button>
                  )}
                </Stack>
              </Grid>

              {selectedSubmission?.submissionId && submissionData && (
                <Grid item xs={12}>
                  <Stack direction={"row"} spacing={1} flexWrap={"wrap"}>
                    <Chip
                      label={`ID: ${
                        submissionData?.id ??
                        selectedSubmission?.submissionId ??
                        "Unknown"
                      }`}
                      size={"small"}
                      variant={"outlined"}
                    />
                    <Chip
                      label={`Saved: ${submissionData?.savedAt ?? "Unknown"}`}
                      size={"small"}
                      variant={"outlined"}
                    />
                    <Chip
                      label={`By: ${submissionData?.savedBy ?? "Unknown"}`}
                      size={"small"}
                      variant={"outlined"}
                    />
                    <Chip
                      label={`Token: ${
                        submissionData?.savedToken?.slice(0, 8) ?? "Unknown"
                      }...`}
                      size={"small"}
                      variant={"outlined"}
                      title={submissionData?.savedToken}
                    />
                  </Stack>
                </Grid>
              )}
            </Grid>
          </Component>

          {isLoading && <BigLoader />}

          {loadError && (
            <Component>
              <Alert severity={"error"}>{loadError}</Alert>
            </Component>
          )}

          {submissionData && !isLoading && (
            <>
              {/* Editor Section */}
              <Component
                title={"State Editor"}
                subtitle={
                  "Edit the submission state directly. Be careful - changes are saved immediately when you click Save."
                }
              >
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Stack
                      direction={"row"}
                      alignItems={"center"}
                      justifyContent={"space-between"}
                    >
                      <Stack
                        direction={"row"}
                        spacing={2}
                        alignItems={"center"}
                      >
                        <ToggleButtonGroup
                          value={differenceToggle ? "Diff" : "Edit"}
                          onChange={() =>
                            setDifferenceToggle(!differenceToggle)
                          }
                          aria-label={
                            "Toggle between edit and diff modes (⌘+E / ⌘+D)"
                          }
                          color={"primary"}
                          size={"small"}
                          exclusive
                        >
                          <ToggleButton
                            value={"Edit"}
                            title={"Edit mode (⌘+E)"}
                          >
                            <EditIcon
                              fontSize={"small"}
                              sx={{ marginRight: "3px" }}
                            />
                            <span style={{ paddingRight: "3px" }}>
                              {"Edit"}
                            </span>
                          </ToggleButton>
                          <ToggleButton
                            value={"Diff"}
                            title={"Diff mode (⌘+D)"}
                          >
                            <DiffIcon
                              fontSize={"small"}
                              sx={{ marginRight: "3px" }}
                            />
                            <span style={{ paddingRight: "2px" }}>
                              {"Diff"}
                            </span>
                          </ToggleButton>
                        </ToggleButtonGroup>
                      </Stack>

                      <Stack
                        direction={"row"}
                        spacing={1}
                        alignItems={"center"}
                      >
                        {!differenceToggle &&
                          newStateText != null &&
                          newStateText !==
                            JSON.stringify(originalState, null, 2) && (
                            <Button
                              variant={"outlined"}
                              color={"secondary"}
                              onClick={() => setNewStateText(null)}
                            >
                              {"Undo all edits"}
                            </Button>
                          )}
                        <Stack direction={"row"} spacing={1}>
                          <CopyButton content={jsonValue} />
                          <EditorPreferenceToggle />
                        </Stack>
                      </Stack>
                    </Stack>
                  </Grid>

                  <Grid item xs={12}>
                    {differenceToggle ? (
                      <SubmissionDiff
                        originalState={originalState}
                        newStateText={
                          newStateText ?? JSON.stringify(originalState, null, 2)
                        }
                        submissionId={selectedSubmission?.submissionId}
                        savedToken={submissionData?.savedToken}
                        companyId={companyId}
                        onSaveSuccess={handleSaveSuccess}
                      />
                    ) : (
                      <CodeEditor
                        value={jsonValue}
                        language={"json"}
                        onChange={(value) => setNewStateText(value)}
                      />
                    )}
                  </Grid>

                  {!differenceToggle &&
                    newStateText != null &&
                    newStateText !== JSON.stringify(originalState, null, 2) && (
                      <Grid item xs={12}>
                        <Alert severity={"warning"}>
                          {
                            "You have unsaved changes. Switch to Diff mode (⌘+D) to review and save."
                          }
                        </Alert>
                      </Grid>
                    )}
                </Grid>
              </Component>
            </>
          )}
        </DashboardContent>
      </AdminDashboard>
    </div>
  );
};

export default SubmissionEditor;
